import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { notificar } from '../utils/notify';
import VinculoBadge from '../components/VinculoBadge';
import ComplementoGuiaExame from '../components/ComplementoGuiaExame';
import AvisoExameColaborador from '../components/AvisoExameColaborador';
import '../styles/bporh_admissoes.css';

// Controle do exame periódico (NR-7).
//
// A Convenia entrega o ASO no array `aso` da LISTAGEM /employees — e só ali: o
// detalhe /employees/{id}, que é o que o sync guarda em dados_json, não traz.
// Por isso existe um sync próprio. Quando ela já tem a data do próximo exame,
// é ela que manda; para quem não tem nada lançado lá, o vencimento é calculado
// pela periodicidade da empresa a partir do último exame (ou da admissão).

const SITUACOES = {
  vencido: { rotulo: 'Vencido', bg: '#fdeaec', cor: '#a11d2b', borda: '#dc3545',
             icone: 'fa-triangle-exclamation' },
  a_vencer: { rotulo: 'A vencer', bg: '#fff5da', cor: '#8a6d00', borda: '#f1be48',
              icone: 'fa-hourglass-half' },
  em_dia: { rotulo: 'Em dia', bg: '#e6f7ef', cor: '#116b47', borda: '#1fa971',
            icone: 'fa-circle-check' },
  sem_data: { rotulo: 'Sem admissão', bg: '#f1f3f5', cor: '#6c757d', borda: '#ced4da',
              icone: 'fa-circle-question' },
  dispensado: { rotulo: 'Não se aplica', bg: '#f1f3f5', cor: '#6c757d', borda: '#ced4da',
                icone: 'fa-minus' },
  // empresa que leva o médico uma vez por ano: sem cobrança individual
  coletivo: { rotulo: 'Coletivo anual', bg: '#e3f6fb', cor: '#0e6e86', borda: '#b3e3ef',
              icone: 'fa-user-doctor' },
};

// "faltam 12 dias" / "venceu há 30 dias" — o número cru não diz o que fazer
const prazoTexto = (dias) => {
  if (dias == null) return '';
  if (dias < 0) return `venceu há ${-dias} dia${-dias === 1 ? '' : 's'}`;
  if (dias === 0) return 'vence hoje';
  return `faltam ${dias} dia${dias === 1 ? '' : 's'}`;
};

const hojeIso = () => new Date().toISOString().slice(0, 10);
const TIPOS_EXAME = { periodico: 'Periódico', admissional: 'Admissional', demissional: 'Demissional',
  retorno: 'Retorno ao Trabalho', mudanca_risco: 'Mudança de riscos ocupacionais' };

// Anotação do operador no colaborador ("médico agendado 12/10", "afastado,
// exame na volta"). Fica na nossa base, não na Convenia. Clique para editar,
// Enter salva, Esc cancela, texto vazio apaga.
const ObsExame = ({ clienteId, conveniaId, obs, onSalvo }) => {
  const API = process.env.REACT_APP_API_BASE;
  const [editando, setEditando] = React.useState(false);
  const [texto, setTexto] = React.useState(obs?.texto || '');
  const [salvando, setSalvando] = React.useState(false);
  React.useEffect(() => { setTexto(obs?.texto || ''); }, [obs, conveniaId]);

  const salvar = () => {
    setSalvando(true);
    axios.post(`${API}/api/bporh/exames/`,
      { cliente_id: clienteId, convenia_id: conveniaId, obs: texto },
      { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => { setEditando(false); if (onSalvo) onSalvo(res.data.obs || null); })
      .finally(() => setSalvando(false));
  };

  if (editando) {
    return (
      <div style={{ minWidth: 220 }}>
        <textarea className="form-control form-control-sm" rows={2} maxLength={1000} autoFocus
          placeholder="Observação sobre o exame deste colaborador..."
          value={texto} onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); salvar(); }
            if (e.key === 'Escape') { setTexto(obs?.texto || ''); setEditando(false); }
          }} />
        <div className="d-flex gap-1 mt-1">
          <button className="btn btn-sm btn-success p-1 px-2" disabled={salvando} onClick={salvar}
            title="Salvar (Enter)">
            <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
          </button>
          <button className="btn btn-sm btn-light border p-1 px-2" disabled={salvando}
            onClick={() => { setTexto(obs?.texto || ''); setEditando(false); }} title="Cancelar (Esc)">
            <i className="fas fa-times text-muted"></i>
          </button>
        </div>
      </div>
    );
  }
  return obs?.texto ? (
    <button type="button" className="btn btn-link btn-sm p-0 text-start" style={{ textDecoration: 'none', maxWidth: 260 }}
      onClick={() => setEditando(true)}
      title={`${obs.texto}${obs.autor ? ` — ${obs.autor}` : ''}${obs.em ? ` (${obs.em})` : ''}`}>
      <span className="small" style={{ color: '#212529' }}>
        <i className="fas fa-comment-dots me-1 text-muted"></i>
        {obs.texto.length > 60 ? `${obs.texto.slice(0, 60)}…` : obs.texto}
      </span>
    </button>
  ) : (
    <button type="button" className="btn btn-sm btn-light border py-0 px-2" onClick={() => setEditando(true)}
      title="Adicionar observação">
      <i className="far fa-comment text-muted" style={{ fontSize: '.75rem' }}></i>
    </button>
  );
};

const BpoRhExamePeriodico = () => {
  const API = process.env.REACT_APP_API_BASE;
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // a empresa vive na URL: o F5 mantém a tela aberta e o link é compartilhável
  const [searchParams, setSearchParams] = useSearchParams();
  const clienteId = searchParams.get('cliente') || '';
  const setClienteId = (v) => setSearchParams(p => {
    const n = new URLSearchParams(p);
    if (v) n.set('cliente', String(v)); else n.delete('cliente');
    return n;
  });
  // 'todos' = todas as empresas numa lista só; o filtro de operadora só faz
  // sentido nesse modo (dentro de uma empresa o operador é um só)
  const modoTodos = clienteId === 'todos';
  const operador = searchParams.get('operador') || '';
  const setOperador = (v) => setSearchParams(p => {
    const n = new URLSearchParams(p);
    if (v) { n.set('operador', v); n.set('cliente', 'todos'); } else n.delete('operador');
    return n;
  });

  const [dados, setDados] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroSituacao, setFiltroSituacao] = useState('');
  // O PCMSO cobre o empregado: PJ, sócio e estagiário não entram. A tela abre
  // só com CLT e Jovem Aprendiz, mas deixa ver o quadro todo para conferir
  // cadastro (um PJ que na verdade é CLT aparece aqui).
  const [verTodos, setVerTodos] = useState(false);

  // ─── Solicitação à medicina do trabalho ───
  // A clínica de cada cliente recebe um e-mail com a guia em PDF (empresa,
  // unidade, CNPJ, dados do colaborador, tipo de exame). Os dados fixos da
  // guia ficam em "Dados da guia"; o texto do e-mail é editável por cliente.
  const [avisoColab, setAvisoColab] = useState(null);   // colaborador do e-mail de orientações
  const [solic, setSolic] = useState(null);        // modal: {itens, tipo, data, operador_id, assunto, corpo, previa}
  const [solicEnviando, setSolicEnviando] = useState(false);
  const [solicCarregando, setSolicCarregando] = useState(false);
  const [guiaCfg, setGuiaCfg] = useState(null);    // modal "Dados da guia"
  const [guiaSalvando, setGuiaSalvando] = useState(false);

  const abrirGuiaCfg = () => {
    axios.get(`${API}/api/bporh/exames/solicitar/`, { params: { cliente_id: clienteId }, headers: headers() })
      .then((res) => setGuiaCfg({ ...res.data.config, grupo_email: res.data.grupo_email, modelos: res.data.modelos || {} }))
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível carregar os dados da guia'}`));
  };
  const salvarGuiaCfg = () => {
    setGuiaSalvando(true);
    const { grupo_email, modelos, ...config } = guiaCfg;
    axios.post(`${API}/api/bporh/exames/solicitar/`, { cliente_id: Number(clienteId), config }, { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); setGuiaCfg(null); })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar'}`))
      .finally(() => setGuiaSalvando(false));
  };

  // abre o modal para um ou vários colaboradores; a prévia do e-mail sai do primeiro
  const abrirSolicitacao = (itens) => {
    if (!itens.length) return;
    setSolicCarregando(true);
    axios.get(`${API}/api/bporh/exames/solicitar/`,
      { params: { cliente_id: itens[0].cliente_id ?? clienteId, convenia_id: itens[0].convenia_id }, headers: headers() })
      .then((res) => {
        const d = res.data;
        setSolic({
          itens, tipo: 'periodico', data: '',
          operador_id: (d.remetentes || [])[0]?.id || '',
          remetentes: d.remetentes || [], config: d.config || {}, grupo_email: d.grupo_email,
          // O operador edita o texto JÁ PREENCHIDO (sem marcadores). Para vários
          // colaboradores, na hora de enviar os dados do 1º voltam a virar
          // marcadores e cada e-mail recebe os seus (desrenderMarcadores).
          assunto: d.email?.assunto || '', corpo: d.email?.corpo || '',
          templateProprio: !!d.template?.proprio, placeholders: d.placeholders || [],
          previa: d.email, ctx: d.ctx || {}, guia: d.guia || null, historico: d.historico || [],
          // destinatário digitado no próprio modal; "Salvar" grava na config do cliente
          destinatario: d.config?.clinica_email || '', destSalvando: false,
        });
      })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível montar a solicitação'}`))
      .finally(() => setSolicCarregando(false));
  };
  const verGuiaPdf = (c) => {
    axios.get(`${API}/api/bporh/exames/solicitar/`, {
      params: { cliente_id: c.cliente_id ?? clienteId, convenia_id: c.convenia_id, pdf: 1,
                tipo: solic?.tipo || 'periodico', data_exame: solic?.data || '' },
      headers: headers(), responseType: 'blob' })
      .then((res) => window.open(URL.createObjectURL(res.data), '_blank'))
      .catch(() => setMensagem('❌ Não foi possível gerar a guia'));
  };
  // grava o e-mail da clínica sem sair do modal (mesma config de "Dados da guia")
  const salvarDestinatario = () => {
    if (!solic) return;
    const email = (solic.destinatario || '').trim();
    if (!email) { setMensagem('Informe o e-mail da clínica'); return; }
    setSolic(s => ({ ...s, destSalvando: true }));
    axios.post(`${API}/api/bporh/exames/solicitar/`,
      { cliente_id: Number(solic.itens[0].cliente_id ?? clienteId), config: { ...solic.config, clinica_email: email } },
      { headers: headers() })
      .then((res) => {
        setMensagem(`✅ Destinatário salvo`);
        setSolic(s => ({ ...s, config: res.data.config, destinatario: res.data.config?.clinica_email || email, destSalvando: false }));
      })
      .catch((err) => {
        setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar o destinatário'}`);
        setSolic(s => ({ ...s, destSalvando: false }));
      });
  };
  // o texto do e-mail é montado ao abrir (tipo Periódico): trocar o tipo troca
  // também a palavra já escrita no assunto e no corpo
  const trocarTipoExame = (s, novo) => {
    const ant = TIPOS_EXAME[s.tipo] || 'Periódico';
    const nov = TIPOS_EXAME[novo] || 'Periódico';
    const troca = (t) => (t || '').split(ant).join(nov).split(ant.toLowerCase()).join(nov.toLowerCase());
    return { ...s, tipo: novo, assunto: troca(s.assunto), corpo: troca(s.corpo),
             ctx: { ...(s.ctx || {}), tipo_exame: nov.toLowerCase(), tipo_exame_titulo: nov } };
  };
  // texto preenchido -> texto com marcadores: os dados do colaborador da prévia
  // voltam a ser {colaborador}, {empresa}... (do valor mais longo ao mais curto,
  // para "São Bernardo" não ser trocado antes de "GaussFleet (São Bernardo...)")
  const desrenderMarcadores = (texto, ctx) => Object.entries(ctx || {})
    .filter(([, v]) => typeof v === 'string' && v.trim().length >= 3)
    .sort((a, b) => b[1].length - a[1].length)
    .reduce((acc, [k, v]) => acc.split(v).join(`{${k}}`), texto || '');

  // "Visualizar e-mail": o e-mail montado (moldura Comece) + a guia em PDF embutida
  const [emailPreview, setEmailPreview] = useState(null);   // {carregando, de, para, cc, assunto, html, anexo, pdfUrl, colaborador}
  const visualizarEmail = (c) => {
    if (!solic) return;
    const alvo = c || solic.itens[0];
    const varios = solic.itens.length > 1;
    setEmailPreview({ carregando: true, colaborador: alvo.nome });
    const corpo = {
      cliente_id: Number(alvo.cliente_id ?? clienteId), convenia_id: alvo.convenia_id, visualizar: true,
      tipo_exame: solic.tipo, data_exame: solic.data || null, operador_id: solic.operador_id || null,
      assunto: varios ? desrenderMarcadores(solic.assunto, solic.ctx) : solic.assunto,
      corpo: varios ? desrenderMarcadores(solic.corpo, solic.ctx) : solic.corpo,
    };
    Promise.all([
      axios.post(`${API}/api/bporh/exames/solicitar/`, corpo, { headers: headers() }),
      axios.get(`${API}/api/bporh/exames/solicitar/`, {
        params: { cliente_id: alvo.cliente_id ?? clienteId, convenia_id: alvo.convenia_id, pdf: 1,
                  tipo: solic.tipo, data_exame: solic.data || '' },
        headers: headers(), responseType: 'blob' }),
    ])
      .then(([email, pdf]) => setEmailPreview({
        ...email.data, colaborador: alvo.nome, carregando: false,
        pdfUrl: URL.createObjectURL(new Blob([pdf.data], { type: 'application/pdf' })),
      }))
      .catch((err) => {
        setEmailPreview(null);
        setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível montar a prévia do e-mail'}`);
      });
  };
  const fecharPreview = () => {
    if (emailPreview?.pdfUrl) URL.revokeObjectURL(emailPreview.pdfUrl);
    setEmailPreview(null);
  };

  const salvarTextoEmail = (restaurar = false) => {
    const corpo = restaurar ? { cliente_id: Number(clienteId), template_restaurar: true }
      : { cliente_id: Number(clienteId),
          template: { assunto: desrenderMarcadores(solic.assunto, solic.ctx), corpo: desrenderMarcadores(solic.corpo, solic.ctx) } };
    axios.post(`${API}/api/bporh/exames/solicitar/`, corpo, { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); abrirSolicitacao(solic.itens); })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar o texto'}`));
  };
  // clínica que atende pelo site: o operador abre o portal, baixa a guia e faz
  // o pedido lá; aqui só fica o registro (a tela mostra "solicitado")
  const registrarPeloPortal = () => {
    if (!solic) return;
    setSolicEnviando(true);
    axios.post(`${API}/api/bporh/exames/solicitar/`, {
      cliente_id: Number(solic.itens[0].cliente_id ?? clienteId),
      convenia_ids: solic.itens.map(c => c.convenia_id),
      tipo_exame: solic.tipo, data_exame: solic.data || null, via: 'portal',
    }, { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); setSolic(null); carregar(); })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível registrar'}`))
      .finally(() => setSolicEnviando(false));
  };

  const enviarSolicitacao = () => {
    if (!solic) return;
    if (!solic.operador_id) { setMensagem('Escolha o operador que assina o envio'); return; }
    if (!solic.config?.clinica_email) { setMensagem('Informe e salve o e-mail da clínica antes de enviar'); return; }
    setSolicEnviando(true);
    axios.post(`${API}/api/bporh/exames/solicitar/`, {
      cliente_id: Number(solic.itens[0].cliente_id ?? clienteId),
      convenia_ids: solic.itens.map(c => c.convenia_id),
      operador_id: solic.operador_id, tipo_exame: solic.tipo, data_exame: solic.data || null,
      // um só colaborador: vai exatamente o que está na tela; vários: cada um recebe os seus dados
      assunto: solic.itens.length > 1 ? desrenderMarcadores(solic.assunto, solic.ctx) : solic.assunto,
      corpo: solic.itens.length > 1 ? desrenderMarcadores(solic.corpo, solic.ctx) : solic.corpo,
    }, { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); setSolic(null); carregar(); })
      .catch((err) => {
        // guia incompleta (sem CPF/RG/nascimento/cargo): abre o preenchimento no modal
        const inc = err.response?.data?.incompletos;
        if (Array.isArray(inc) && inc.length) {
          setSolic((x) => (x ? { ...x, incompletos: inc } : x));
          setEmailPreview(null);
        }
        setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível enviar'}`);
      })
      .finally(() => setSolicEnviando(false));
  };

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/bporh/exames/`,
      { params: clienteId ? { cliente_id: clienteId,
                              ...(verTodos ? { vinculo: 'todos' } : {}),
                              ...(modoTodos && operador ? { operador } : {}) } : {},
        headers: headers() })
      .then((res) => {
        setDados(res.data);
        setClientes(res.data.clientes || []);
      })
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.erro || 'Não foi possível carregar os exames'}`))
      .finally(() => setCarregando(false));
  }, [clienteId, verTodos, operador]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [carregar]);

  const colaboradores = useMemo(() => {
    const lista = dados?.colaboradores || [];
    const b = busca.trim().toLowerCase();
    return lista.filter(c => {
      if (filtroSituacao && c.situacao !== filtroSituacao) return false;
      if (b && ![c.nome, c.cargo, c.departamento, c.cpf, c.cliente, c.obs?.texto]
        .filter(Boolean).join(' ').toLowerCase().includes(b)) return false;
      return true;
    });
    // o backend já entrega ordenado por nome; a urgência manda na visão
  }, [dados, busca, filtroSituacao]);

  const ordenados = useMemo(() => {
    const peso = { vencido: 0, a_vencer: 1, sem_data: 2, em_dia: 3, dispensado: 4, coletivo: 5 };
    return [...colaboradores].sort((a, z) =>
      (peso[a.situacao] - peso[z.situacao])
      || ((a.dias_para_vencer ?? 99999) - (z.dias_para_vencer ?? 99999)));
  }, [colaboradores]);

  // ── registrar exame ──
  const [registrando, setRegistrando] = useState(null);   // colaborador
  const [form, setForm] = useState({ data: '', clinica: '', obs: '', status: 'Realizado' });
  const [salvando, setSalvando] = useState(false);

  const abrirRegistro = (c) => {
    setRegistrando(c);
    setForm({ data: hojeIso(), clinica: '', obs: '', status: 'Realizado' });
  };

  const salvarExame = () => {
    if (!form.data) { setMensagem('❌ Informe a data'); return; }
    setSalvando(true);
    const corpo = {
      // no modo 'todos' cada linha sabe de que empresa é
      cliente_id: registrando.cliente_id ?? Number(clienteId), convenia_id: registrando.convenia_id,
      status: form.status, clinica: form.clinica, obs: form.obs,
    };
    if (form.status === 'Agendado') corpo.data_prevista = form.data;
    else corpo.data_realizada = form.data;
    axios.post(`${API}/api/bporh/exames/`, corpo, { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); setRegistrando(null); carregar(); })
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível registrar'}`))
      .finally(() => setSalvando(false));
  };

  // `col` é o colaborador dono do registro: em "todos" o clienteId da URL não
  // serve como empresa, e o backend agora casa o id do exame com o cliente
  const removerRegistro = (reg, col) => {
    axios.delete(`${API}/api/bporh/exames/`,
      { params: { cliente_id: col?.cliente_id ?? reg.cliente_id ?? clienteId, id: reg.id },
        headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); carregar(); })
      .catch(() => setMensagem('❌ Não foi possível remover'));
  };

  // ── periodicidade da empresa ──
  const [editPeriod, setEditPeriod] = useState(false);
  const [periodo, setPeriodo] = useState(12);
  useEffect(() => { setPeriodo(dados?.periodicidade_meses || 12); }, [dados]);

  // regime da empresa: individual (vencimento por pessoa) x coletivo anual
  // (médico na empresa examina todo mundo de uma vez — sem cobrança individual)
  const [salvandoRegime, setSalvandoRegime] = useState(false);
  const salvarRegime = (regime) => {
    setSalvandoRegime(true);
    axios.post(`${API}/api/bporh/exames/`,
      { cliente_id: Number(clienteId), regime }, { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); carregar(); })
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível salvar o regime'}`))
      .finally(() => setSalvandoRegime(false));
  };

  const salvarPeriodicidade = () => {
    axios.post(`${API}/api/bporh/exames/`,
      { cliente_id: Number(clienteId), periodicidade_meses: Number(periodo) },
      { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); setEditPeriod(false); carregar(); })
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível salvar'}`));
  };

  // A Convenia guarda o ASO na ficha do colaborador e devolve isso na LISTAGEM
  // /employees (não no detalhe). Este botão traz esse histórico para cá.
  const [sincronizando, setSincronizando] = useState(false);
  const [progSync, setProgSync] = useState('');
  const sincronizar = () => {
    setSincronizando(true);
    axios.post(`${API}/api/bporh/exames/`,
      { cliente_id: Number(clienteId), sincronizar: true }, { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); carregar(); })
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível sincronizar'}`))
      .finally(() => setSincronizando(false));
  };
  // visão "todas as empresas": sincroniza uma a uma (o endpoint é por cliente)
  const sincronizarTodos = async (lista) => {
    setSincronizando(true);
    let exames = 0; const falhas = [];
    try {
      for (let i = 0; i < lista.length; i += 1) {
        const c = lista[i];
        setProgSync(`${i + 1}/${lista.length} · ${c.cliente}`);
        try {
          const res = await axios.post(`${API}/api/bporh/exames/`,
            { cliente_id: Number(c.id), sincronizar: true }, { headers: headers() });
          exames += Number(res.data.sincronizados || 0);
        } catch (err) {
          falhas.push(`${c.cliente}: ${err.response?.data?.mensagem || 'falhou'}`);
        }
      }
    } finally {
      setSincronizando(false); setProgSync('');
    }
    setMensagem(`${falhas.length ? '⚠️' : '✅'} ${exames} exame(s) trazido(s) de ${lista.length - falhas.length} empresa(s)`
      + (falhas.length ? ` · ${falhas.join(' · ')}` : ''));
    carregar();
  };

  const [historicoAberto, setHistoricoAberto] = useState(null);
  // troca a obs de UM colaborador em memória — recarregar a tela toda por uma
  // anotação faria a lista piscar (e no modo 'todos' são 13 consultas)
  const trocarObs = (c, novaObs) => setDados(d => d ? ({
    ...d,
    colaboradores: (d.colaboradores || []).map(x =>
      (x.convenia_id === c.convenia_id && (x.cliente_id ?? null) === (c.cliente_id ?? null))
        ? { ...x, obs: novaObs } : x),
  }) : d);
  const painel = dados?.painel || {};
  const clienteNome = modoTodos
    ? `Todas as empresas${operador ? ` · ${operador}` : ''}`
    : clientes.find(c => String(c.id) === String(clienteId))?.cliente;
  // empresas da operadora escolhida (bporh_cliente.operador é texto livre e
  // traz duplas como 'Fran/Vitória' — casa por pedaço, sem acento)
  const norm = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const daOperadora = (c) => !operador || (c.operador || '').split(/[,/;|&]+| e /)
    .some(tk => norm(tk).trim() && (norm(operador).startsWith(norm(tk).trim()) || norm(tk).trim().startsWith(norm(operador))));
  const clientesVisiveis = clientes.filter(daOperadora);

  return (
    <div className="bporh-admissoes-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-notes-medical header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              BPO RH — Exame Periódico
              {clienteNome && <span className="text-muted"> · {clienteNome}</span>}
            </h2>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {clienteId && !modoTodos && (
              <>
                <button className="btn btn-outline-secondary btn-sm" onClick={abrirGuiaCfg}
                  title="Empresa, unidade, CNPJ, cidades e e-mail da clínica que vão na guia de exame">
                  <i className="fas fa-clinic-medical me-2"></i>Dados da guia
                </button>
                {(() => {
                  const vencidos = (dados?.colaboradores || []).filter(c => c.situacao === 'vencido');
                  return vencidos.length > 0 && (
                    <button className="btn btn-danger btn-sm" disabled={solicCarregando}
                      onClick={() => abrirSolicitacao(vencidos)}
                      title="Envia à clínica um e-mail por colaborador vencido, cada um com a sua guia em PDF">
                      <i className={`fas ${solicCarregando ? 'fa-spinner fa-spin' : 'fa-envelope-open-text'} me-2`}></i>
                      Solicitar exame dos vencidos ({vencidos.length})
                    </button>
                  );
                })()}
              </>
            )}
            {clienteId && (
              <button className="btn btn-primary btn-sm"
                onClick={() => (modoTodos ? sincronizarTodos(clientesVisiveis) : sincronizar())}
                disabled={sincronizando}
                title={modoTodos
                  ? 'Traz da Convenia os exames de todas as empresas da lista, uma por uma'
                  : 'Traz da Convenia os exames já lançados na ficha de cada colaborador'}>
                <i className={`fas ${sincronizando ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-down'} me-2`}></i>
                {sincronizando ? `Sincronizando${progSync ? ` ${progSync}` : '...'}` : 'Buscar da Convenia'}
              </button>
            )}
            <button className="btn btn-outline-primary btn-sm" onClick={carregar} disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>
              Atualizar
            </button>
          </div>
        </div>

        <div className="alert alert-light border small mb-3">
          <i className="fas fa-info-circle me-2 text-primary"></i>
          Os exames vêm da <b>Convenia</b> (ficha do colaborador → Documentos → Exame
          periódico). Quando ela já traz a data do próximo exame, é ela que vale; para
          quem não tem nada lançado lá, o vencimento é calculado como{' '}
          <b>{dados?.periodicidade_meses || 12} meses</b> após o último exame — ou após a
          admissão. Use <b>Buscar da Convenia</b> para atualizar, e <b>Registrar</b> para
          lançar aqui o que ainda não foi lançado lá. A lista traz apenas{' '}
          <b>CLT e Jovem Aprendiz</b> — o PCMSO é obrigação do empregador, e PJ, sócio
          e estagiário não entram no programa da empresa.
          {dados?.fora_da_regra > 0 && !dados?.ver_todos && (
            <span className="d-block mt-1" style={{ color: '#8a6d00' }}>
              <i className="fas fa-user-slash me-1"></i>
              {dados.fora_da_regra} colaborador(es) fora da regra por vínculo — troque o
              filtro para <b>Todos os vínculos</b> se quiser conferir o cadastro deles.
            </span>
          )}
          {dados?.aso_sincronizado_em && (
            <span className="d-block mt-1 text-muted">
              <i className="fas fa-clock me-1"></i>
              {dados.aso_total} exame(s) da Convenia · última busca em {dados.aso_sincronizado_em}
            </span>
          )}
        </div>

        {/* filtros */}
        <div className="row g-3 mb-3">
          <div className="col-md-2">
            <label className="form-label small fw-bold mb-1 text-muted">Operadora</label>
            <select className="form-select form-select-sm" value={operador}
              onChange={(e) => setOperador(e.target.value)}
              title="Filtra as empresas da carteira desta operadora (abre a visão de todas as empresas)">
              <option value="">Todas as operadoras</option>
              {(dados?.operadores || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label small fw-bold mb-1 text-muted">Empresa</label>
            <select className="form-select form-select-sm" value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}>
              <option value="">Selecione a empresa...</option>
              <option value="todos">— Todas as empresas{operador ? ` da ${operador}` : ''} —</option>
              {clientesVisiveis.map(c => (
                <option key={c.id} value={c.id}>{c.cliente}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label small fw-bold mb-1 text-muted">Situação</label>
            <select className="form-select form-select-sm" value={filtroSituacao}
              onChange={(e) => setFiltroSituacao(e.target.value)} disabled={!clienteId}>
              <option value="">Todas as situações</option>
              <option value="vencido">Vencidos</option>
              <option value="a_vencer">A vencer ({dados?.dias_alerta || 60} dias)</option>
              <option value="em_dia">Em dia</option>
              <option value="dispensado">Não se aplica</option>
              <option value="coletivo">Coletivo anual</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label small fw-bold mb-1 text-muted">Vínculo</label>
            <select className="form-select form-select-sm" value={verTodos ? 'todos' : 'clt'}
              onChange={(e) => setVerTodos(e.target.value === 'todos')} disabled={!clienteId}
              title="O exame periódico é obrigação do empregador — PJ, sócio e estagiário não entram no PCMSO da empresa">
              <option value="clt">CLT e Jovem Aprendiz</option>
              <option value="todos">Todos os vínculos (conferir cadastro)</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label small fw-bold mb-1 text-muted">Buscar</label>
            <input type="text" className="form-control form-control-sm"
              placeholder={modoTodos ? 'Nome, empresa, cargo...' : 'Nome, cargo, CPF...'}
              value={busca} onChange={(e) => setBusca(e.target.value)} disabled={!clienteId} />
          </div>
        </div>

        {clienteId && !modoTodos && dados?.regime === 'coletivo' && (
          <div className="alert alert-light border small mb-3" style={{ borderLeft: '4px solid #00b5cc' }}>
            <i className="fas fa-user-doctor me-2" style={{ color: '#0e6e86' }}></i>
            <b>Exame coletivo anual.</b> Nesta empresa o médico vai até lá uma vez por ano e
            examina todo o quadro — por isso ninguém aparece como vencido ou a vencer. As datas
            individuais continuam na lista só para referência.
          </div>
        )}

        {!clienteId ? (
          <div className="alert alert-info">
            <i className="fas fa-hand-point-up me-2"></i>
            Escolha a empresa para ver a agenda de exames periódicos.
          </div>
        ) : carregando ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary"></div>
            <p className="text-muted mt-2">Calculando os vencimentos...</p>
          </div>
        ) : (
          <>
            <div className="adm-resumo mb-3">
              <div className="adm-kpi">
                <span className="v">{painel.total || 0}</span>
                <span className="k">Colaboradores ativos</span>
              </div>
              <div className={`adm-kpi ${painel.vencido ? 'pend' : ''}`}>
                <span className="v">{painel.vencido || 0}</span>
                <span className="k">Exame vencido</span>
              </div>
              <div className={`adm-kpi ${painel.a_vencer ? 'pend' : ''}`}>
                <span className="v">{painel.a_vencer || 0}</span>
                <span className="k">A vencer ({dados?.dias_alerta || 60}d)</span>
              </div>
              <div className="adm-kpi">
                <span className="v">{painel.em_dia || 0}</span>
                <span className="k">Em dia</span>
              </div>
            </div>

            {/* periodicidade da empresa (por empresa: no modo 'todos' só informa) */}
            {modoTodos ? (
              <div className="alert alert-light border small mb-3">
                <i className="fas fa-building me-2 text-primary"></i>
                Visão consolidada de <b>{(dados?.empresas_no_recorte || []).length}</b> empresa(s)
                {dados?.operador_filtro ? <> da carteira de <b>{dados.operador_filtro}</b></> : ''}.
                A periodicidade e a busca na Convenia são ajustadas dentro de cada empresa.
              </div>
            ) : (
            <div className="bg-white rounded shadow-sm p-3 mb-3"
              style={{ borderLeft: '4px solid #00b5cc' }}>
              {editPeriod ? (
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="fw-bold">Exame a cada</span>
                  <input type="number" min="1" max="60"
                    className="form-control form-control-sm" style={{ width: 90 }}
                    value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
                  <span className="fw-bold">meses</span>
                  <button className="btn btn-sm btn-success" onClick={salvarPeriodicidade}>
                    <i className="fas fa-check me-1"></i>Salvar
                  </button>
                  <button className="btn btn-sm btn-light border"
                    onClick={() => { setEditPeriod(false); setPeriodo(dados?.periodicidade_meses || 12); }}>
                    Cancelar
                  </button>
                </div>
              ) : (
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <i className="fas fa-repeat" style={{ color: '#00b5cc' }}></i>
                  <span>
                    Periodicidade desta empresa:{' '}
                    <b>a cada {dados?.periodicidade_meses || 12} meses</b>
                  </span>
                  <span className="text-muted mx-1">·</span>
                  <span>Regime:</span>
                  <div className="btn-group btn-group-sm" role="group">
                    <button type="button" disabled={salvandoRegime}
                      className={`btn ${dados?.regime !== 'coletivo' ? 'btn-primary' : 'btn-light border'}`}
                      onClick={() => salvarRegime('individual')}
                      title="Cada colaborador tem o próprio vencimento (padrão NR-7)">
                      <i className="fas fa-user me-1"></i>individual
                    </button>
                    <button type="button" disabled={salvandoRegime}
                      className={`btn ${dados?.regime === 'coletivo' ? 'btn-primary' : 'btn-light border'}`}
                      onClick={() => salvarRegime('coletivo')}
                      title="O médico vai à empresa uma vez por ano e examina todo mundo — sem cobrança por pessoa">
                      <i className="fas fa-user-doctor me-1"></i>coletivo anual
                    </button>
                  </div>
                  <button className="btn btn-sm btn-light border"
                    onClick={() => setEditPeriod(true)}
                    title="A NR-7 prevê prazos diferentes por risco e faixa etária">
                    <i className="fas fa-pen me-1"></i>alterar
                  </button>
                </div>
              )}
            </div>
            )}

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead style={{ background: '#f8f9fa' }}>
                  <tr>
                    <th>Colaborador</th>
                    {modoTodos && <th>Empresa</th>}
                    <th>Cargo</th>
                    <th>Admissão</th>
                    <th>Último exame</th>
                    <th>Vence em</th>
                    <th>Situação</th>
                    <th style={{ minWidth: 200 }}>Obs</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {ordenados.map(c => {
                    const st = SITUACOES[c.situacao] || SITUACOES.sem_data;
                    return (
                      <tr key={`${c.cliente_id ?? clienteId}-${c.convenia_id}`}>
                        <td>
                          <div className="fw-bold">{c.nome}</div>
                          <div className="small text-muted">
                            {c.departamento || '—'}
                            {c.vinculo && <VinculoBadge vinculo={c.vinculo} />}
                          </div>
                        </td>
                        {modoTodos && (
                          <td className="small">
                            <div className="fw-semibold">{c.cliente}</div>
                            <div className="text-muted">{c.operador || ''}</div>
                          </td>
                        )}
                        <td className="small">{c.cargo || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {c.admissao || '—'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {c.ultimo_exame || <span className="text-muted">nenhum</span>}
                          {/* ASO não-periódico pendente na Convenia (admissional de
                              anos atrás nunca concluído): explica o "nenhum" e aponta
                              o que acertar lá — não é vencimento */}
                          {c.aso_pendente_outro && (
                            <div className="small" style={{ color: '#8a6d00' }}
                              title="Este ASO está pendente na Convenia e não conta como exame feito. Conclua ou exclua lá.">
                              <i className="fas fa-cloud me-1"></i>{c.aso_pendente_outro}
                            </div>
                          )}
                          {c.total_exames > 1 && (
                            <span className="badge bg-light text-muted border ms-2">
                              {c.total_exames} no total
                            </span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {c.vencimento || '—'}
                          <div className="small text-muted">{prazoTexto(c.dias_para_vencer)}</div>
                          {c.vencimento_origem === 'convenia' ? (
                            <span className="badge bg-light text-muted border"
                              style={{ fontSize: '.62rem' }}
                              title="Data que já está lançada na Convenia">
                              <i className="fas fa-cloud me-1"></i>Convenia
                            </span>
                          ) : (
                            <span className="badge bg-light text-muted border"
                              style={{ fontSize: '.62rem' }}
                              title={`Calculado: ${dados?.periodicidade_meses || 12} meses após a ${c.vencimento_origem}`}>
                              <i className="fas fa-calculator me-1"></i>{c.vencimento_origem}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="badge" style={{
                            backgroundColor: st.bg, color: st.cor,
                            border: `1.5px solid ${st.borda}` }}>
                            <i className={`fas ${st.icone} me-1`}></i>{st.rotulo}
                          </span>
                          {c.agendado_para && (
                            <div className="small text-muted mt-1">
                              <i className="fas fa-calendar-check me-1"></i>
                              agendado {c.agendado_para}
                            </div>
                          )}
                          {c.solicitacao && (
                            <div className="small mt-1" style={{ color: '#0e6e86' }}
                              title={`Solicitação de exame ${c.solicitacao.tipo} enviada à clínica${c.solicitacao.operador ? ` por ${c.solicitacao.operador}` : ''}${c.solicitacao.data_exame ? ` (sugerido ${c.solicitacao.data_exame})` : ''}`}>
                              <i className={`fas ${c.solicitacao.canal === 'portal' ? 'fa-globe' : 'fa-envelope-circle-check'} me-1`}></i>
                              solicitado {c.solicitacao.em}{c.solicitacao.canal === 'portal' ? ' (portal)' : ''}
                            </div>
                          )}
                          {c.aviso_colaborador && (
                            <div className="small mt-1" style={{ color: '#116b47' }}
                              title={`Orientações enviadas ao colaborador${c.aviso_colaborador.operador ? ` por ${c.aviso_colaborador.operador}` : ''}${c.aviso_colaborador.data_atendimento ? ` — atendimento em ${c.aviso_colaborador.data_atendimento}` : ''}`}>
                              <i className="fas fa-user-check me-1"></i>
                              avisado {c.aviso_colaborador.em}
                            </div>
                          )}
                        </td>
                        <td>
                          <ObsExame clienteId={c.cliente_id ?? Number(clienteId)} conveniaId={c.convenia_id}
                            obs={c.obs} onSalvo={(o) => trocarObs(c, o)} />
                        </td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                          {c.situacao !== 'coletivo' && c.situacao !== 'dispensado' && (
                            <button className="btn btn-sm btn-outline-primary me-1" disabled={solicCarregando}
                              onClick={() => abrirSolicitacao([c])}
                              title={`Pedir à clínica o agendamento do exame de ${c.nome} (e-mail com a guia em PDF)`}>
                              <i className="fas fa-envelope me-1"></i>Solicitar
                            </button>
                          )}
                          {c.situacao !== 'coletivo' && c.situacao !== 'dispensado' && (
                            <button className="btn btn-sm btn-outline-secondary me-1"
                              onClick={() => setAvisoColab(c)}
                              title={`E-mail a ${c.nome} com as orientações do atendimento (clínica, endereço, horário e o kit em anexo)`}>
                              <i className="fas fa-paper-plane me-1"></i>Avisar
                            </button>
                          )}
                          <button className="btn btn-sm btn-outline-success me-1"
                            onClick={() => abrirRegistro(c)}
                            title={`Registrar exame de ${c.nome}`}>
                            <i className="fas fa-notes-medical me-1"></i>Registrar
                          </button>
                          {c.historico.length > 0 && (
                            <button className="btn btn-sm btn-light border"
                              onClick={() => setHistoricoAberto(
                                historicoAberto === c.convenia_id ? null : c.convenia_id)}
                              title="Ver o histórico de exames">
                              <i className="fas fa-clock-rotate-left text-muted"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {ordenados.length === 0 && (
                    <tr><td colSpan={modoTodos ? 9 : 8} className="text-center text-muted py-4">
                      <i className="fas fa-inbox me-2"></i>Nenhum colaborador neste filtro.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {historicoAberto && (() => {
              const c = ordenados.find(x => x.convenia_id === historicoAberto);
              if (!c) return null;
              return (
                <div className="bg-white rounded shadow-sm p-3 mt-2"
                  style={{ borderLeft: '4px solid #00205b' }}>
                  <h6 className="fw-bold mb-2">
                    <i className="fas fa-clock-rotate-left me-2" style={{ color: '#00205b' }}></i>
                    Histórico de {c.nome}
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <tbody>
                        {(c.exames_convenia || []).map((e, i) => (
                          <tr key={`conv-${i}`} style={{ background: '#f8fbff' }}>
                            <td style={{ whiteSpace: 'nowrap' }}>{e.data || '—'}</td>
                            <td>
                              <span className="badge bg-light text-muted border">
                                {e.motivo || 'Exame'}
                              </span>
                            </td>
                            <td className="small">
                              {e.concluido
                                ? <span style={{ color: '#116b47' }}>
                                    <i className="fas fa-circle-check me-1"></i>concluído
                                  </span>
                                : <span style={{ color: '#8a6d00' }}>
                                    <i className="fas fa-hourglass-half me-1"></i>pendente
                                  </span>}
                            </td>
                            <td className="small text-muted">{e.obs || ''}</td>
                            <td className="small text-muted">
                              <i className="fas fa-cloud me-1"></i>Convenia
                            </td>
                            <td></td>
                          </tr>
                        ))}
                        {c.historico.map(h => (
                          <tr key={h.id}>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {h.data_realizada || h.data_prevista || '—'}
                            </td>
                            <td><span className="badge bg-light text-muted border">{h.status}</span></td>
                            <td className="small">{h.clinica || '—'}</td>
                            <td className="small text-muted">{h.obs || ''}</td>
                            <td className="small text-muted">
                              {h.autor ? `${h.autor} · ${h.criado_em}` : h.criado_em}
                            </td>
                            <td className="text-end">
                              <button className="btn btn-sm btn-light border p-1 px-2"
                                onClick={() => removerRegistro(h, c)}
                                title="Remover este registro">
                                <i className="fas fa-trash text-danger"
                                  style={{ fontSize: '.72rem' }}></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* ─── MODAL: DADOS DA GUIA (empresa + clínica) ─── */}
      {guiaCfg && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 620 }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-clinic-medical me-2"></i>Dados da guia de exame
                  </h5>
                  <small style={{ color: '#6c757d' }}>{clienteNome} · vão impressos em toda guia enviada à clínica</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setGuiaCfg(null)} disabled={guiaSalvando}></button>
              </div>
              <div className="modal-body">
                <div className="row g-2">
                  <div className="col-12">
                    <label className="form-label small fw-bold mb-1 text-muted">Modelo da guia (formulário da clínica)</label>
                    <select className="form-select form-select-sm" value={guiaCfg.modelo || 'padrao'}
                      onChange={(e) => setGuiaCfg(g => ({ ...g, modelo: e.target.value }))}>
                      {Object.entries(guiaCfg.modelos || { padrao: 'Guia de encaminhamento' }).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                  {[
                    ['clinica_nome', 'Clínica de medicina do trabalho', 'Ex.: Prevenção — Medicina e Segurança do Trabalho', 12],
                    ['clinica_email', 'E-mail da clínica (destinatário)', 'agendamento@clinica.com.br', 12],
                    ['clinica_copia', 'Cópia (CC) em todo pedido à clínica', 'pessoa@cliente.com.br; equipe@cliente.com.br', 12],
                    ['clinica_portal', 'Link do portal da clínica (quando o pedido é feito no site dela)', 'https://portal.clinica.com.br', 12],
                    ['empresa', 'Empresa (razão social na guia)', '', 6],
                    ['cnpj', 'CNPJ da unidade', '00.000.000/0000-00', 6],
                    ['unidade', 'Unidade', 'Ex.: São Bernardo do Campo/SP', 6],
                    ['cidade_agendamento', 'Cidade de agendamento', 'Ex.: Santo André/SP', 6],
                    ['cidade_exame', 'Cidade de realização do exame', 'Se vazio, usa a de agendamento', 6],
                    ['responsavel', 'Responsável pelo preenchimento', 'Nome de quem assina a guia', 6],
                  ].map(([campo, rotulo, ph, col]) => (
                    <div className={`col-md-${col}`} key={campo}>
                      <label className="form-label small fw-bold mb-1 text-muted">{rotulo}</label>
                      <input type={campo === 'clinica_email' ? 'email' : 'text'} className="form-control form-control-sm"
                        placeholder={ph} value={guiaCfg[campo] || ''}
                        onChange={(e) => setGuiaCfg(g => ({ ...g, [campo]: e.target.value }))} />
                    </div>
                  ))}
                </div>
                <div className="form-check mt-3">
                  <input className="form-check-input" type="checkbox" id="exigeRg" checked={Boolean(guiaCfg.exige_rg)}
                    onChange={(e) => setGuiaCfg(g => ({ ...g, exige_rg: e.target.checked }))} />
                  <label className="form-check-label small" htmlFor="exigeRg">
                    <b>A clínica exige RG</b> — sem ele a guia não é enviada (CPF, nascimento e cargo são sempre obrigatórios)
                  </label>
                </div>
                <div className="form-check mt-2">
                  <input className="form-check-input" type="checkbox" id="cidadeResidencia"
                    checked={Boolean(guiaCfg.cidade_residencia)}
                    onChange={(e) => setGuiaCfg(g => ({ ...g, cidade_residencia: e.target.checked }))} />
                  <label className="form-check-label small" htmlFor="cidadeResidencia">
                    <b>Exame na cidade onde o colaborador mora</b> — a cidade de agendamento e de exame da guia
                    vem do endereço dele na Convenia (sem endereço, usa as cidades acima)
                  </label>
                </div>
                <p className="small text-muted mt-3 mb-0">
                  <i className="fas fa-circle-info me-1"></i>
                  O e-mail sai do Gmail do operador escolhido no envio
                  {guiaCfg.grupo_email ? <>, com cópia ao grupo do cliente (<b>{guiaCfg.grupo_email}</b>)</> : ''}.
                  A clínica responde direto ao operador.
                </p>
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setGuiaCfg(null)} disabled={guiaSalvando}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button className="btn btn-primary" onClick={salvarGuiaCfg} disabled={guiaSalvando}>
                  <i className={`fas ${guiaSalvando ? 'fa-spinner fa-spin' : 'fa-save'} me-2`}></i>Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: SOLICITAR EXAME À CLÍNICA ─── */}
      {solic && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-envelope-open-text me-2"></i>Solicitar exame à medicina do trabalho
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {solic.itens.length === 1 ? solic.itens[0].nome : `${solic.itens.length} colaboradores`}
                    {' · '}{solic.config?.clinica_nome || 'clínica não cadastrada'}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setSolic(null)} disabled={solicEnviando}></button>
              </div>
              <div className="modal-body">
                {solic.config?.clinica_portal && (
                  <div className="alert alert-info small py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                      <i className="fas fa-globe me-2"></i>
                      <b>{solic.config.clinica_nome || 'Esta clínica'}</b> recebe o pedido pelo portal dela: abra o site,
                      baixe a guia de cada colaborador (botão <i className="fas fa-file-pdf text-danger"></i> na lista) e faça a
                      solicitação lá. Depois clique em <b>Marcar como solicitado</b> para registrar aqui.
                    </div>
                    <a className="btn btn-sm btn-primary" href={solic.config.clinica_portal} target="_blank" rel="noreferrer">
                      <i className="fas fa-arrow-up-right-from-square me-2"></i>Abrir portal da clínica
                    </a>
                  </div>
                )}
                {!solic.config?.clinica_email && !solic.config?.clinica_portal && (
                  <div className="alert alert-warning small py-2">
                    <i className="fas fa-triangle-exclamation me-2"></i>
                    Este cliente ainda não tem o e-mail da clínica. Informe em <b>Para (clínica)</b> abaixo e clique em
                    Salvar — a guia em PDF já pode ser conferida.
                  </div>
                )}
                <div className="row g-2 mb-3">
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1 text-muted">Tipo de exame</label>
                    <select className="form-select form-select-sm" value={solic.tipo}
                      onChange={(e) => setSolic(s => trocarTipoExame(s, e.target.value))}>
                      <option value="periodico">Periódico</option>
                      <option value="admissional">Admissional</option>
                      <option value="demissional">Demissional</option>
                      <option value="retorno">Retorno ao Trabalho</option>
                      <option value="mudanca_risco">Mudança de riscos ocupacionais</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1 text-muted">Data sugerida <span className="text-muted fw-normal">(opcional)</span></label>
                    <input type="date" className="form-control form-control-sm" value={solic.data}
                      onChange={(e) => setSolic(s => ({ ...s, data: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1 text-muted">Quem assina o envio</label>
                    <select className="form-select form-select-sm" value={solic.operador_id}
                      onChange={(e) => setSolic(s => ({ ...s, operador_id: e.target.value }))}>
                      <option value="">Escolha o operador...</option>
                      {solic.remetentes.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="small fw-bold text-muted mb-1">
                    Colaboradores ({solic.itens.length}) — um e-mail e uma guia para cada
                  </div>
                  <div className="border rounded" style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {solic.itens.map(c => (
                      <div key={c.convenia_id} className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom small">
                        <div>
                          <b>{c.nome}</b> <span className="text-muted">· {c.cargo || '—'} · vence {c.vencimento || '—'}</span>
                          {solic.itens.length === 1 && solic.guia && (
                            <div className="text-muted" style={{ fontSize: '.75rem' }}>
                              <i className="fas fa-building me-1"></i>
                              {solic.guia.unidade || 'unidade não informada'}{solic.guia.cnpj ? ` · CNPJ ${solic.guia.cnpj}` : ''}
                              {solic.guia.cidade_agendamento ? ` · ${solic.guia.cidade_agendamento}` : ''}
                              {solic.guia.cidade_da_residencia && <span className="ms-1" title="A guia usa a cidade do endereço do colaborador na Convenia">(cidade onde mora)</span>}
                              {solic.guia.outra_unidade && <span className="ms-1" title="Filial do colaborador na Convenia, diferente da unidade configurada em Dados da guia">(filial da Convenia)</span>}
                            </div>
                          )}
                          {c.solicitacao && <span className="badge bg-light text-muted border ms-2">já solicitado {c.solicitacao.em}</span>}
                        </div>
                        <div className="d-flex gap-1">
                          <button className="btn btn-sm btn-light border py-0" onClick={() => verGuiaPdf(c)} title="Ver a guia em PDF">
                            <i className="fas fa-file-pdf text-danger"></i>
                          </button>
                          {solic.itens.length > 1 && (
                            <button className="btn btn-sm btn-light border py-0" title="Tirar da lista"
                              onClick={() => setSolic(s => ({ ...s, itens: s.itens.filter(x => x.convenia_id !== c.convenia_id) }))}>
                              <i className="fas fa-xmark text-muted"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label small fw-bold mb-1 text-muted">
                    Para (clínica) <span className="fw-normal">— mais de um e-mail: separe por ;</span>
                  </label>
                  <div className="input-group input-group-sm">
                    <input type="text" className="form-control"
                      placeholder="agendamento@clinica.com.br; coordenacao@clinica.com.br"
                      value={solic.destinatario}
                      onChange={(e) => setSolic(s => ({ ...s, destinatario: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') salvarDestinatario(); }} />
                    <button className="btn btn-outline-primary" onClick={salvarDestinatario}
                      disabled={solic.destSalvando || !solic.destinatario.trim()
                        || solic.destinatario.trim() === (solic.config?.clinica_email || '')}
                      title="Grava como destinatário padrão deste cliente (o mesmo de 'Dados da guia')">
                      <i className={`fas ${solic.destSalvando ? 'fa-spinner fa-spin' : 'fa-save'} me-1`}></i>Salvar
                    </button>
                  </div>
                  <div className="form-text">
                    {solic.config?.clinica_email
                      ? <>Salvo: <b>{solic.config.clinica_email}</b></>
                      : <span className="text-danger">Sem destinatário salvo — salve antes de enviar.</span>}
                    {solic.grupo_email && <> · cópia: {solic.grupo_email}</>}
                    {solic.destinatario.trim() !== (solic.config?.clinica_email || '') && solic.destinatario.trim() && (
                      <span className="text-warning ms-1"><i className="fas fa-triangle-exclamation me-1"></i>alteração não salva</span>
                    )}
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div className="small fw-bold text-muted">
                    Texto do e-mail{solic.itens.length > 1 && <span className="fw-normal"> — mostrado para {solic.itens[0].nome}; nome, CPF, filial e CNPJ são trocados em cada envio</span>}
                  </div>
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-link p-0" onClick={() => salvarTextoEmail(false)}
                      title="Grava este texto como padrão deste cliente (os marcadores continuam sendo preenchidos no envio)">
                      salvar como padrão do cliente
                    </button>
                    {solic.templateProprio && (
                      <button className="btn btn-sm btn-link p-0 text-muted" onClick={() => salvarTextoEmail(true)}>
                        restaurar padrão
                      </button>
                    )}
                  </div>
                </div>
                <input type="text" className="form-control form-control-sm mb-2" value={solic.assunto}
                  onChange={(e) => setSolic(s => ({ ...s, assunto: e.target.value }))} />
                <textarea className="form-control form-control-sm" rows={7} value={solic.corpo}
                  onChange={(e) => setSolic(s => ({ ...s, corpo: e.target.value }))} />
                <div className="form-text">
                  É este texto que vai no e-mail, com a guia em PDF anexa. Edite à vontade; "salvar como padrão do
                  cliente" guarda o modelo para as próximas solicitações.
                </div>
                {(solic.incompletos || []).length > 0 && (
                  <div className="mt-3">
                    <div className="small fw-bold mb-2 text-danger">
                      <i className="fas fa-ban me-1"></i>
                      O envio parou: {solic.incompletos.length} guia(s) incompleta(s). Preencha e envie de novo.
                    </div>
                    {solic.incompletos.map((x) => (
                      <div key={x.convenia_id}>
                        <div className="small fw-bold mb-1">{x.nome}</div>
                        <ComplementoGuiaExame clienteId={solic.itens[0].cliente_id ?? clienteId}
                          conveniaId={x.convenia_id} faltando={x.faltando}
                          onSalvo={(r) => setSolic((s2) => (s2 ? {
                            ...s2,
                            incompletos: (s2.incompletos || [])
                              .map((y) => (y.convenia_id === x.convenia_id ? { ...y, faltando: r.faltando || [] } : y))
                              .filter((y) => y.faltando.length > 0),
                          } : s2))} />
                      </div>
                    ))}
                  </div>
                )}
                {solic.historico?.length > 0 && (
                  <div className="small text-muted mt-2">
                    <i className="fas fa-clock-rotate-left me-1"></i>
                    Últimos envios de {solic.itens[0].nome}: {solic.historico.map(h => `${h.tipo} em ${h.em}`).join(' · ')}
                  </div>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-outline-primary me-auto" onClick={() => visualizarEmail()}
                  disabled={solicEnviando || emailPreview?.carregando}
                  title={solic.itens.length > 1 ? `Mostra o e-mail montado para ${solic.itens[0].nome}, com a guia em PDF` : 'Mostra o e-mail montado, com a guia em PDF'}>
                  <i className={`fas ${emailPreview?.carregando ? 'fa-spinner fa-spin' : 'fa-eye'} me-2`}></i>Visualizar e-mail
                </button>
                <button className="btn btn-secondary" onClick={() => setSolic(null)} disabled={solicEnviando}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                {solic.config?.clinica_portal && (
                  <button className="btn btn-success" onClick={registrarPeloPortal} disabled={solicEnviando || !solic.itens.length}
                    title="Registra que o pedido foi feito no portal da clínica (não envia e-mail)">
                    <i className={`fas ${solicEnviando ? 'fa-spinner fa-spin' : 'fa-check'} me-2`}></i>
                    Marcar como solicitado pelo portal
                  </button>
                )}
                <button className="btn btn-primary" onClick={enviarSolicitacao}
                  disabled={solicEnviando || !solic.itens.length || !solic.config?.clinica_email}>
                  {solicEnviando
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Enviando...</>
                    : <><i className="fas fa-paper-plane me-2"></i>Enviar {solic.itens.length > 1 ? `${solic.itens.length} e-mails` : 'e-mail'} com a guia</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {avisoColab && (
        <AvisoExameColaborador
          clienteId={avisoColab.cliente_id ?? Number(clienteId)}
          colaborador={avisoColab}
          onFechar={() => setAvisoColab(null)}
          onEnviado={(m) => { setMensagem(`✅ ${m}`); setAvisoColab(null); carregar(); }}
          onErro={(m) => setMensagem(`❌ ${m}`)}
          onAviso={(m) => setMensagem(`✅ ${m}`)} />
      )}

      {/* ─── MODAL: PRÉVIA DO E-MAIL (com a guia) ─── */}
      {emailPreview && !emailPreview.carregando && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10001 }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-eye me-2"></i>Prévia do e-mail
                  </h5>
                  <small style={{ color: '#6c757d' }}>{emailPreview.colaborador} · é assim que a clínica vai receber</small>
                </div>
                <button type="button" className="btn-close" onClick={fecharPreview}></button>
              </div>
              <div className="modal-body p-0">
                <div className="px-3 py-2 small border-bottom" style={{ background: '#f8f9fa' }}>
                  <div><span className="text-muted" style={{ display: 'inline-block', width: 70 }}>De:</span> {emailPreview.de}</div>
                  <div><span className="text-muted" style={{ display: 'inline-block', width: 70 }}>Para:</span> {emailPreview.para}</div>
                  {emailPreview.cc && <div><span className="text-muted" style={{ display: 'inline-block', width: 70 }}>Cc:</span> {emailPreview.cc}</div>}
                  {emailPreview.bcc && <div><span className="text-muted" style={{ display: 'inline-block', width: 70 }}>Cco:</span> {emailPreview.bcc}</div>}
                  <div><span className="text-muted" style={{ display: 'inline-block', width: 70 }}>Assunto:</span> <b>{emailPreview.assunto}</b></div>
                  <div>
                    <span className="text-muted" style={{ display: 'inline-block', width: 70 }}>Anexo:</span>
                    <i className="fas fa-file-pdf text-danger me-1"></i>{emailPreview.anexo}
                    {emailPreview.pdfUrl && (
                      <a className="ms-2" href={emailPreview.pdfUrl} target="_blank" rel="noreferrer">abrir em outra aba</a>
                    )}
                  </div>
                </div>
                <iframe title="E-mail" srcDoc={emailPreview.html} sandbox=""
                  style={{ width: '100%', height: 520, border: 0, background: '#f4f6fa' }} />
                <div className="px-3 py-2 small fw-bold text-muted border-top border-bottom" style={{ background: '#f8f9fa' }}>
                  <i className="fas fa-paperclip me-2"></i>Anexo — {emailPreview.anexo}
                </div>
                {emailPreview.pdfUrl && (
                  <iframe title="Guia em PDF" src={emailPreview.pdfUrl}
                    style={{ width: '100%', height: 620, border: 0 }} />
                )}
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={fecharPreview}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: REGISTRAR EXAME ─── */}
      {registrando && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-notes-medical me-2"></i>Registrar exame
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {registrando.nome} · vence em {registrando.vencimento || '—'}
                  </small>
                </div>
                <button type="button" className="btn-close"
                  onClick={() => setRegistrando(null)} disabled={salvando}></button>
              </div>
              <div className="modal-body">
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold mb-1 text-muted">Situação</label>
                    <select className="form-select form-select-sm" value={form.status}
                      onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}>
                      <option value="Realizado">Realizado</option>
                      <option value="Agendado">Agendado</option>
                      <option value="Não se aplica">Não se aplica</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-bold mb-1 text-muted">
                      {form.status === 'Agendado' ? 'Data agendada' : 'Data do exame'}
                    </label>
                    <input type="date" className="form-control form-control-sm"
                      value={form.data}
                      onChange={(e) => setForm(f => ({ ...f, data: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-bold mb-1 text-muted">Clínica</label>
                    <input type="text" className="form-control form-control-sm"
                      placeholder="Ex.: Mednet, SESI..."
                      value={form.clinica}
                      onChange={(e) => setForm(f => ({ ...f, clinica: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-bold mb-1 text-muted">Observação</label>
                    <input type="text" className="form-control form-control-sm"
                      placeholder="Opcional"
                      value={form.obs}
                      onChange={(e) => setForm(f => ({ ...f, obs: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') salvarExame(); }} />
                  </div>
                </div>
                {form.status === 'Realizado' && (
                  <p className="small text-muted mt-3 mb-0">
                    <i className="fas fa-circle-info me-1"></i>
                    O próximo vencimento passa a contar desta data:{' '}
                    <b>{dados?.periodicidade_meses || 12} meses</b> depois.
                  </p>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setRegistrando(null)}
                  disabled={salvando}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button className="btn btn-success" onClick={salvarExame} disabled={salvando}>
                  {salvando
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Salvando...</>
                    : <><i className="fas fa-check me-2"></i>Registrar</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BpoRhExamePeriodico;
